"use client";

import { ReactNode } from "react";
import "./TextEmphasis.css";

import { csx, handleInternalNavigation, isUrlExternal } from "../../../utils";
import { useTheme } from "../../ThemeProvider";
import { TooltipTrigger, TooltipTriggerProps } from "../../Tooltip";

interface TextEmphasisProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * The URL that the text emphasis should link to. Turns the element into an `a` tag.
   */
  to?: string;
  /**
   * The content to display within the text emphasis.
   */
  children: ReactNode;
  /**
   * Props to pass to the tooltip trigger. When provided, the text emphasis will be wrapped in a tooltip trigger.
   */
  tooltipProps?: Omit<TooltipTriggerProps, "children">;
  /**
   * A test ID.
   */
  "data-testid"?: string;
}

/**
 * A component that displays text with emphasis. Provide tooltipProps to wrap the text in a tooltip trigger.
 * @interface TextEmphasisProps
 */
const TextEmphasis = ({
  to,
  children,
  tooltipProps,
  "data-testid": testId,
}: TextEmphasisProps) => {
  const { className: themeClassName } = useTheme();

  const content = tooltipProps ? (
    <TooltipTrigger delay={50} closeDelay={75} {...tooltipProps}>
      {children}
    </TooltipTrigger>
  ) : (
    children
  );

  if (to) {
    const isExternal = isUrlExternal(to);
    return (
      <span
        className={csx(
          "proton-TextEmphasis",
          tooltipProps && "proton-TextEmphasis--tooltip",
          themeClassName
        )}
      >
        <a
          data-testid={testId}
          href={to}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          onClick={
            !isExternal ? (e) => handleInternalNavigation(e, to) : undefined
          }
        >
          {content}
        </a>
      </span>
    );
  }

  return (
    <span
      className={csx(
        "proton-TextEmphasis",
        tooltipProps && "proton-TextEmphasis--tooltip",
        themeClassName
      )}
      data-testid={testId}
    >
      {content}
    </span>
  );
};

export default TextEmphasis;
